import { useState } from 'react';
import clsx from 'clsx';
import { DoorOpen, DoorClosed, ShieldAlert, Lock, Unlock } from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { Badge } from '../components/Badge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { useToast } from '../components/Toast.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { Stagger, StaggerItem } from '../components/motion.tsx';
import './Doors.css';

export function Doors() {
  const doors = trpc.door.list.useQuery();

  if (doors.isLoading) {
    return (
      <div className="doors-grid">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card door-card">
            <div className="door-card__head">
              <div className="skel" style={{ width: 42, height: 42, borderRadius: 'var(--r-md)' }} />
              <div className="door-card__title-wrap">
                <div className="skel" style={{ height: 15, width: 140, marginBottom: 10 }} />
                <div className="skel" style={{ height: 11, width: 90 }} />
              </div>
            </div>
            <div className="door-card__actions">
              <div className="skel" style={{ height: 32, width: 120, borderRadius: 'var(--r-md)' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!doors.data || doors.data.length === 0) {
    return (
      <EmptyState
        icon={DoorOpen}
        title="No doors yet"
        body="Once you register a controller and the relay agent comes online, its doors will appear here."
      />
    );
  }

  return (
    <Stagger className="doors-grid">
      {doors.data.map((d) => (
        <StaggerItem key={d.id}>
          <DoorCard door={d} />
        </StaggerItem>
      ))}
    </Stagger>
  );
}

interface DoorRow {
  id: string;
  name: string;
  state: 'normal' | 'unlocked_temp' | 'lockdown' | 'open' | 'forced' | 'held_open';
  isLockdown: boolean;
  unlockTimeSeconds: number;
}

function DoorCard({ door }: { door: DoorRow }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const confirm = useConfirm();
  const unlock = trpc.door.unlock.useMutation({
    onSuccess: () => utils.door.list.invalidate(),
  });
  const lockdown = trpc.door.lockdown.useMutation({
    onSuccess: () => {
      utils.door.list.invalidate();
      toast.warn(`Lockdown engaged on ${door.name}`);
    },
    onError: (e) => toast.error('Lockdown failed', e.message),
  });
  const release = trpc.door.releaseLockdown.useMutation({
    onSuccess: () => {
      utils.door.list.invalidate();
      toast.success(`Lockdown released on ${door.name}`);
    },
    onError: (e) => toast.error('Release failed', e.message),
  });
  const [recent, setRecent] = useState<'unlocking' | 'unlocked' | 'failed' | null>(null);

  async function onUnlock() {
    setRecent('unlocking');
    try {
      await Promise.race([
        unlock.mutateAsync({ doorId: door.id, durationSeconds: door.unlockTimeSeconds }),
        new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 5000)),
      ]);
      setRecent('unlocked');
      toast.success(`${door.name} unlocked`, `${door.unlockTimeSeconds}s`);
      setTimeout(() => setRecent(null), 2000);
    } catch (e) {
      setRecent('failed');
      toast.error(`Failed to unlock ${door.name}`, (e as Error).message);
      setTimeout(() => setRecent(null), 3000);
    }
  }

  async function onLockdown() {
    const ok = await confirm({
      title: `Engage lockdown on ${door.name}?`,
      body: (
        <>
          This immediately secures the door and refuses all credentials until the lockdown is
          released. Use only for an active security event.
        </>
      ),
      confirmLabel: 'Engage lockdown',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (ok) lockdown.mutate({ doorId: door.id });
  }

  const isOpen = door.state === 'open' || door.state === 'held_open' || door.state === 'forced';
  const glyphTone = door.isLockdown ? 'is-lockdown' : isOpen ? 'is-open' : '';

  return (
    <div className={clsx('card', 'door-card', door.isLockdown && 'is-lockdown')}>
      <div className="door-card__head">
        <div
          className={clsx('door-card__glyph', glyphTone, recent === 'unlocking' && 'is-unlocking')}
          aria-hidden="true"
        >
          {door.isLockdown ? (
            <Lock size={19} />
          ) : isOpen ? (
            <DoorOpen size={19} />
          ) : (
            <DoorClosed size={19} />
          )}
        </div>
        <div className="door-card__title-wrap">
          <div className="door-card__name" title={door.name}>
            {door.name}
          </div>
          <div className="door-card__meta">
            <span className="door-card__telemetry">unlock {door.unlockTimeSeconds}s</span>
            {door.isLockdown ? (
              <Badge tone="danger">lockdown</Badge>
            ) : (
              <Badge tone="ok">{door.state}</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="door-card__actions">
        {door.isLockdown ? (
          <button
            className="secondary"
            disabled={release.isPending}
            onClick={() => void release.mutate({ doorId: door.id })}
          >
            <Unlock size={14} />
            Release lockdown
          </button>
        ) : (
          <>
            <button
              className="danger"
              disabled={lockdown.isPending}
              onClick={() => void onLockdown()}
              title="Engage lockdown"
            >
              <ShieldAlert size={14} />
              Lockdown
            </button>
            <button
              className={clsx(
                'door-card__unlock-btn',
                recent === 'unlocked' && 'is-unlocked',
                recent === 'failed' && 'is-failed',
              )}
              onClick={() => void onUnlock()}
              disabled={recent === 'unlocking'}
            >
              <Unlock size={14} />
              {recent === 'unlocking'
                ? 'Unlocking…'
                : recent === 'unlocked'
                  ? 'Unlocked'
                  : recent === 'failed'
                    ? 'Failed'
                    : 'Unlock'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
